/*
 * Qikify filter animation (shared). Motion for any filterable / sortable list marked with data-qk-filter* attributes.
 * Usage, markup, attributes, CSS variables, pitfalls and how to test are documented at the top of assets/qikify-filter-anim.css.
 *
 * What it does, in one paragraph: the page's own filter code stays in charge of WHAT is shown (it hides, shows and re-orders the items
 * however it likes). It only wraps that DOM change in QikifyFilterAnim.update(root, mutateFn). The component measures every item, calls
 * mutateFn (so the DOM is in its final state before anything is drawn), measures again and then decorates the change with the Web
 * Animations API: items that left fade out as a light stand-in at their old place (the real element is already hidden by the caller),
 * items that stay glide from their old to their new place (FLIP, transform only), items that appear fade in and rise a few px with a
 * light stagger (the fade uses filter: opacity(), not opacity, because card styles often pin opacity with !important and an
 * !important declaration beats every animation), and the root's height is animated when the list gets shorter so the page below does
 * not jump. Nothing is ever left in an inline style (apart from the temporary height lock, which is restored in finish()), so a
 * failed animation can never leave an item invisible.
 *
 * Cost is bounded on purpose: only items near the viewport are animated, at most ghostMax stand-ins are created (default 12, a stand-in
 * is a small shell or a pruned copy, never a full deep clone of a heavy card), and when more than maxChanged items would animate (default
 * 24) the transition is skipped and the caller's change simply appears (like reduced motion). A slow commit (mutateFn > 100ms) gets no
 * stand-ins, only the glide / enter. The safety timer starts when the motion starts, after the commit and the second measurement.
 *
 * It never breaks the caller: update() NEVER throws because of motion (bad tuning variables are replaced by the defaults, and any error
 * while measuring or animating ends in a full cleanup and the normal result). The one exception that does come through is an exception
 * thrown by the caller's own mutateFn: that propagates exactly as it would without the component.
 *
 * It never animates: on the first render (the root gets data-qk-filter-ready two frames after load), with prefers-reduced-motion,
 * inside the Theme Editor (Shopify.designMode), when the root has data-qk-filter-animate="false", or when the root is not on screen.
 * In all those cases update() just calls mutateFn, so the page behaves exactly as if the component were not there.
 *
 * It only looks inside the roots that carry data-qk-filter, adds no listener on a live storefront (the Theme Editor listeners are
 * added only when Shopify.designMode is on), and does nothing on a page where no element opts in.
 */
(function () {
  'use strict';
  if (window.QikifyFilterAnim) return;

  var reducedQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reducedMotion = function () { return !!(reducedQuery && reducedQuery.matches); };
  var designMode = function () { return !!(window.Shopify && window.Shopify.designMode); };

  // The defaults live here (not in the CSS), so a variable set on any ancestor of the root (body, :root, the section) is read as is.
  var DEFAULTS = {
    leaveDuration: 90, leaveEase: 'ease-out', leaveScale: 0.98,
    moveDuration: 200, moveEase: 'cubic-bezier(.2,.7,.2,1)',
    enterDuration: 180, enterEase: 'ease-out', enterOffset: 8,
    stagger: 10, staggerMax: 8,
    heightDuration: 200, heightEase: 'ease',
    near: 200,
    ghostMax: 12, ghostBudget: 40, maxChanged: 24
  };
  // Allowed range of each tuning value; anything outside (or not a number / not a valid easing) falls back to the default.
  var LIMITS = {
    leaveDuration: [0, 2000], moveDuration: [0, 2000], enterDuration: [0, 2000], heightDuration: [0, 2000],
    leaveScale: [0.5, 1.5], enterOffset: [-200, 200], stagger: [0, 200], staggerMax: [0, 50], near: [0, 5000],
    ghostMax: [0, 50], ghostBudget: [0, 500], maxChanged: [0, 500]
  };
  var SLOW_COMMIT_MS = 100; // a caller change slower than this gets no stand-ins (the motion still plays)
  var HARD_MAX_MS = 5000;   // nothing stays decorated longer than this after the commit, whatever happens (also covers a hidden tab, where no frame ever comes)

  // Reads --qk-filter-<name>: a time in ms (or s), a plain number, or a string; "" when unset or empty.
  function readRaw(style, name) { return String(style.getPropertyValue('--qk-filter-' + name) || '').trim(); }
  function inRange(n, key) { var lim = LIMITS[key]; return isFinite(n) && n >= lim[0] && n <= lim[1]; }
  function readNumber(style, name, key, kind) {
    var raw = readRaw(style, name);
    if (!raw) return DEFAULTS[key];
    var n = parseFloat(raw);
    if (!isFinite(n)) return DEFAULTS[key];
    if (kind === 'time') n = /ms$/i.test(raw) ? n : /s$/i.test(raw) ? n * 1000 : n;
    return inRange(n, key) ? n : DEFAULTS[key];
  }
  // A whole-number option that can also be set by an attribute on the root (the attribute wins over the CSS variable).
  function readCount(root, style, attr, name, key) {
    var a = root.getAttribute(attr);
    if (a !== null && String(a).trim() !== '') {
      var n = parseFloat(a);
      if (inRange(n, key) && n % 1 === 0) return n;
    }
    var v = readNumber(style, name, key);
    return v % 1 === 0 ? v : DEFAULTS[key]; // whole numbers only: 0.5 is not "0", it is ignored
  }
  function validEase(v) {
    if (!v) return false;
    if (window.CSS && typeof CSS.supports === 'function') return CSS.supports('transition-timing-function', v);
    return /^(linear|ease|ease-in|ease-out|ease-in-out|cubic-bezier\([^)]*\)|steps\([^)]*\))$/i.test(v);
  }
  function readEase(style, name, key) {
    var raw = readRaw(style, name);
    return validEase(raw) ? raw : DEFAULTS[key];
  }
  function settings(root) {
    var cs = getComputedStyle(root);
    return {
      leaveDuration: readNumber(cs, 'leave-duration', 'leaveDuration', 'time'),
      leaveEase: readEase(cs, 'leave-ease', 'leaveEase'),
      leaveScale: readNumber(cs, 'leave-scale', 'leaveScale'),
      moveDuration: readNumber(cs, 'move-duration', 'moveDuration', 'time'),
      moveEase: readEase(cs, 'move-ease', 'moveEase'),
      enterDuration: readNumber(cs, 'enter-duration', 'enterDuration', 'time'),
      enterEase: readEase(cs, 'enter-ease', 'enterEase'),
      enterOffset: readNumber(cs, 'enter-offset', 'enterOffset'),
      stagger: readNumber(cs, 'stagger', 'stagger', 'time'),
      staggerMax: (function (v) { return v % 1 === 0 ? v : DEFAULTS.staggerMax; })(readNumber(cs, 'stagger-max', 'staggerMax')),
      heightDuration: readNumber(cs, 'height-duration', 'heightDuration', 'time'),
      heightEase: readEase(cs, 'height-ease', 'heightEase'),
      near: readNumber(cs, 'near', 'near'),
      ghostMax: readCount(root, cs, 'data-qk-filter-ghost-max', 'ghost-max', 'ghostMax'),
      ghostBudget: readCount(root, cs, 'data-qk-filter-ghost-budget', 'ghost-budget', 'ghostBudget'),
      maxChanged: readCount(root, cs, 'data-qk-filter-max-changed', 'max-changed', 'maxChanged')
    };
  }

  var ITEM = '[data-qk-filter-item]';
  var running = typeof WeakMap === 'function' ? new WeakMap() : null; // root -> its running transition
  var qsa = function (root) { return Array.prototype.slice.call(root.querySelectorAll(ITEM)); };
  var isShown = function (el) { return !!el && el.getClientRects().length > 0; };

  function canAnimate(root) {
    return !!(running && root && root.hasAttribute && root.hasAttribute('data-qk-filter') &&
      root.getAttribute('data-qk-filter-animate') !== 'false' && root.hasAttribute('data-qk-filter-ready') &&
      typeof root.animate === 'function' && typeof Map === 'function' && !reducedMotion() && !designMode() && isShown(root));
  }

  // Ends the transition running on a root (the DOM is already in its final state, so this only removes the decoration).
  function settle(root) {
    var tx = running && running.get(root);
    if (tx) tx.finish();
  }

  // Everything a transition adds to the page, and the one function that removes it all.
  function Transition(root) {
    this.root = root;
    this.anims = [];
    this.ghosts = [];
    this.timer = null;
    this.hardTimer = null;
    this.rearmed = false;
    this.finished = false;
    this.heightLocked = false;
    this.prevHeight = '';
    this.prevHeightPriority = '';
  }
  Transition.prototype.finish = function () {
    if (this.finished) return;
    this.finished = true;
    var root = this.root;
    clearTimeout(this.timer);
    clearTimeout(this.hardTimer);
    this.anims.forEach(function (a) { try { a.cancel(); } catch (err) { /* already gone */ } });
    this.ghosts.forEach(function (g) { try { if (g.parentNode) g.parentNode.removeChild(g); } catch (err) { /* already gone */ } });
    if (this.heightLocked) { // give the root back the inline height it had before (usually none)
      if (this.prevHeight) root.style.setProperty('height', this.prevHeight, this.prevHeightPriority);
      else root.style.removeProperty('height');
      if (!root.getAttribute('style')) root.removeAttribute('style');
    }
    root.removeAttribute('data-qk-filter-busy');
    if (running.get(root) === this) running.delete(root);
  };

  function update(root, mutate) {
    if (typeof mutate !== 'function') return undefined;
    if (!root || !root.hasAttribute || !root.hasAttribute('data-qk-filter')) return mutate();
    settle(root); // every call starts from a settled DOM, so rapid calls never stack

    // 1. first positions. Any problem here means: no motion, just the caller's change.
    var ctx = null;
    try {
      if (canAnimate(root)) ctx = measureBefore(root);
    } catch (err) { ctx = null; }
    if (!ctx) return mutate();

    // 2. the caller changes the DOM (hide / show / re-order / add / remove) in one go. Its own exceptions propagate (nothing to clean up yet).
    var m0 = performance.now();
    var result = mutate();
    ctx.mutateMs = performance.now() - m0;

    // 3. last positions + motion. An error anywhere in here ends in a full cleanup and is never rethrown.
    var tx = new Transition(root);
    try {
      play(root, ctx, tx);
    } catch (err) {
      tx.finish();
    }
    return result;
  }

  function measureBefore(root) {
    var cfg = settings(root);
    var vh = window.innerHeight;
    var sy0 = window.pageYOffset, sx0 = window.pageXOffset;
    var rootRect = root.getBoundingClientRect();
    var before = new Map();
    qsa(root).forEach(function (el) {
      var shown = isShown(el);
      var info = { shown: shown };
      if (shown) {
        var r = el.getBoundingClientRect();
        info.rect = { left: r.left, top: r.top, width: r.width, height: r.height };
        info.doc = { left: r.left + sx0, top: r.top + sy0 };
        info.near = r.bottom > -cfg.near && r.top < vh + cfg.near;
        // display is only needed to rebuild a stand-in, and only items near the viewport can get one (no per-item style read for the rest)
        if (info.near) info.display = getComputedStyle(el).display;
      }
      before.set(el, info);
    });
    return { cfg: cfg, vh: vh, sy0: sy0, sx0: sx0, rootRect: rootRect, H0: rootRect.height, before: before, mutateMs: 0 };
  }

  // ---- stand-ins for leaving items -------------------------------------------------------------------------------------------------
  // The real element is already hidden by the caller. The stand-in sits on its old box and only has to look right for ~90ms while it fades.
  function copyBoxStyle(g, cs) {
    g.style.backgroundColor = cs.backgroundColor;
    if (cs.backgroundImage && cs.backgroundImage !== 'none') g.style.backgroundImage = cs.backgroundImage;
    g.style.borderTop = cs.borderTop;
    g.style.borderRight = cs.borderRight;
    g.style.borderBottom = cs.borderBottom;
    g.style.borderLeft = cs.borderLeft;
    g.style.borderRadius = cs.borderRadius;
    g.style.boxShadow = cs.boxShadow;
  }
  // A shell: an empty element with the item's box, background, border, radius and shadow. Very cheap, whatever the item contains.
  function makeShell(el) {
    var g = document.createElement(el.tagName);
    copyBoxStyle(g, getComputedStyle(el));
    return g;
  }
  var inertDoc = null;
  // A pruned copy, only for small items (node budget): no scripts, styles, templates, media or frames, hidden parts or parts marked
  // data-qk-filter-ghost-skip; no ids. It must never cause a network request: every image is pinned to the file the ORIGINAL image
  // already loaded (its currentSrc, so the browser's cache serves it), and an image that has not loaded gets no src at all.
  function makePrunedClone(el) {
    var origImgs = el.querySelectorAll('img');
    // cloneNode in the live document starts downloading every <img> at once (even lazy ones in hidden parts), before we can strip
    // anything: copy into an inert document (no browsing context, so nothing loads), prune there, adopt the result at the end
    inertDoc = inertDoc || document.implementation.createHTMLDocument('');
    var c = inertDoc.importNode(el, true);
    var imgs = c.querySelectorAll('img');
    Array.prototype.forEach.call(imgs, function (n, i) {
      var o = origImgs[i];
      var loaded = o && o.complete && o.naturalWidth > 0 && o.currentSrc;
      n.removeAttribute('srcset'); n.removeAttribute('sizes'); n.removeAttribute('loading'); n.removeAttribute('src');
      if (loaded) n.setAttribute('src', loaded);
    });
    Array.prototype.forEach.call(c.querySelectorAll('script,style,template,noscript,source,picture source,video,audio,iframe,object,embed,link,[hidden],[data-qk-filter-ghost-skip]'), function (n) {
      if (n.parentNode) n.parentNode.removeChild(n);
    });
    Array.prototype.forEach.call(c.querySelectorAll('[id]'), function (n) { n.removeAttribute('id'); });
    // an inline background-image url() on a copied node would be a request too: drop the image part of inline styles
    Array.prototype.forEach.call(c.querySelectorAll('[style*="url("]'), function (n) { n.style.backgroundImage = 'none'; });
    c.removeAttribute('hidden');
    c.removeAttribute('id');
    c.removeAttribute('data-qk-filter-item');
    return document.adoptNode(c);
  }
  function makeGhost(root, rootRect, el, b, shell, budget) {
    var g = null, isClone = false;
    if (!shell && budget > 0 && el.getElementsByTagName('*').length <= budget) { g = makePrunedClone(el); isClone = true; }
    if (!g) g = makeShell(el);
    // inert / aria-hidden / pointer-events on the ghost root only (a subtree is never touched node by node)
    g.setAttribute('aria-hidden', 'true');
    g.setAttribute('inert', '');
    g.setAttribute('data-qk-filter-ghost', '');
    g.style.setProperty('display', isClone ? b.display : 'block', 'important');
    g.style.position = 'absolute';
    g.style.margin = '0';
    g.style.boxSizing = 'border-box';
    g.style.pointerEvents = 'none';
    g.style.zIndex = '1';
    g.style.left = (b.rect.left - rootRect.left - root.clientLeft) + 'px';
    g.style.top = (b.rect.top - rootRect.top - root.clientTop) + 'px';
    g.style.width = b.rect.width + 'px';
    g.style.height = b.rect.height + 'px';
    return g;
  }
  // ranking for stand-ins: items that intersect the viewport first (the one nearest the top first), then the near ones off screen by distance
  function ghostRank(b, vh) {
    var top = b.rect.top, bottom = top + b.rect.height;
    if (bottom > 0 && top < vh) return Math.max(0, top); // on screen: 0 .. vh
    return vh + (bottom <= 0 ? -bottom : top - vh);      // off screen (still near): after every on-screen item
  }

  function play(root, ctx, tx) {
    var cfg = ctx.cfg, before = ctx.before, rootRect = ctx.rootRect, H0 = ctx.H0;
    var near = function (r) { return r.bottom > -cfg.near && r.top < ctx.vh + cfg.near; };

    // last positions (all reads first, no writes in between)
    var cur = window.pageYOffset;
    var after = new Map();
    var staying = [], entering = [], leaving = [];
    qsa(root).forEach(function (el) {
      if (!isShown(el)) return;
      var r = el.getBoundingClientRect();
      var b = before.get(el);
      after.set(el, { rect: r, doc: { left: r.left + window.pageXOffset, top: r.top + cur } });
      if (b && b.shown) staying.push(el); else entering.push(el);
    });
    before.forEach(function (b, el) { if (b.shown && !after.has(el)) leaving.push(el); });

    var moved = staying.filter(function (el) {
      var b = before.get(el), a = after.get(el);
      return Math.abs(b.doc.left - a.doc.left) > 1 || Math.abs(b.doc.top - a.doc.top) > 1;
    });
    var H1 = root.getBoundingClientRect().height;
    if (!leaving.length && !entering.length && !moved.length) return; // nothing visible changed

    // what would actually be animated: only items near the viewport count
    var movedNear = moved.filter(function (el) { return before.get(el).near || near(after.get(el).rect); });
    var enteringNear = entering.filter(function (el) { return near(after.get(el).rect); });
    var leavingNear = leaving.filter(function (el) { return before.get(el).near; });
    var animCount = movedNear.length + enteringNear.length + leavingNear.length;
    if (!animCount) return; // everything that changed is far from the screen: nothing to show
    // too much would change at once: skip the motion, the caller's change simply appears (no cost, same as reduced motion)
    if (cfg.maxChanged > 0 && animCount > cfg.maxChanged) return;

    // stand-ins for leaving items: at most ghostMax, the ones on screen and nearest to the top first; shells when there are many
    var ghostEls = [];
    if (cfg.ghostMax > 0 && ctx.mutateMs <= SLOW_COMMIT_MS && leavingNear.length) {
      ghostEls = leavingNear.slice().sort(function (x, y) { return ghostRank(before.get(x), ctx.vh) - ghostRank(before.get(y), ctx.vh); }).slice(0, cfg.ghostMax);
    }
    var useShells = ghostEls.length > cfg.ghostMax / 2;
    var leaveDelay = ghostEls.length ? cfg.leaveDuration : 0;

    // from here on there is decoration to clean up: tx.finish() is the way out, whatever happens
    running.set(root, tx);
    root.setAttribute('data-qk-filter-busy', '');
    var longest = Math.max(cfg.leaveDuration, cfg.moveDuration, cfg.enterDuration + cfg.staggerMax * cfg.stagger, cfg.heightDuration);
    var span = leaveDelay + longest + 250;
    // Safety net. Hard bound first (armed now, before any animation exists): at most HARD_MAX_MS after the commit. The normal timer is armed
    // at the first frame after the commit (a heavy first frame, e.g. a big re-layout, must not eat the animation's time) and, when it fires
    // while animations are still advancing, it gives them one more span before everything is force-finished.
    tx.hardTimer = setTimeout(function () { tx.finish(); }, HARD_MAX_MS);
    var armNormal = function () {
      if (tx.finished) return;
      tx.timer = setTimeout(function () {
        if (!tx.rearmed && tx.anims.some(function (a) { return a.playState === 'running'; })) {
          tx.rearmed = true;
          tx.timer = setTimeout(function () { tx.finish(); }, span);
        } else tx.finish();
      }, span);
    };
    if (window.requestAnimationFrame) requestAnimationFrame(armNormal); else armNormal();

    // height: when the list gets shorter, the old height is kept until the new one has been animated in (no layout jump, no scroll clamp).
    // When it gets taller nothing is locked: the new cards fade in over the space they now occupy.
    var lockHeight = (H0 - H1) > 1;
    if (lockHeight) {
      tx.heightLocked = true;
      tx.prevHeight = root.style.getPropertyValue('height');
      tx.prevHeightPriority = root.style.getPropertyPriority('height');
      root.style.height = H0 + 'px';
      if (window.pageYOffset !== ctx.sy0) window.scrollTo(ctx.sx0, ctx.sy0);
    }

    // leaving items: the stand-in at the old place fades out (writes only from here on)
    ghostEls.forEach(function (el) {
      var g = makeGhost(root, rootRect, el, before.get(el), useShells, cfg.ghostBudget);
      root.appendChild(g);
      tx.ghosts.push(g);
      tx.anims.push(g.animate(
        [{ filter: 'opacity(1)', transform: 'scale(1)' }, { filter: 'opacity(0)', transform: 'scale(' + cfg.leaveScale + ')' }],
        { duration: cfg.leaveDuration, easing: cfg.leaveEase, fill: 'forwards' }
      ));
    });

    // staying items glide (FLIP, transform only); during the leave they sit at their old place (fill: backwards)
    movedNear.forEach(function (el) {
      var b = before.get(el), a = after.get(el);
      var dx = b.doc.left - a.doc.left, dy = b.doc.top - a.doc.top;
      tx.anims.push(el.animate(
        [{ transform: 'translate(' + dx + 'px,' + dy + 'px)' }, { transform: 'none' }],
        { duration: cfg.moveDuration, delay: leaveDelay, easing: cfg.moveEase, fill: 'backwards' }
      ));
    });

    // entering items fade in and rise a few px, light stagger on the first few
    var k = 0;
    enteringNear.forEach(function (el) {
      tx.anims.push(el.animate(
        [{ filter: 'opacity(0)', transform: 'translateY(' + cfg.enterOffset + 'px)' }, { filter: 'opacity(1)', transform: 'none' }],
        { duration: cfg.enterDuration, delay: leaveDelay + Math.min(k++, cfg.staggerMax) * cfg.stagger, easing: cfg.enterEase, fill: 'backwards' }
      ));
    });

    if (lockHeight) {
      tx.anims.push(root.animate(
        [{ height: H0 + 'px' }, { height: H1 + 'px' }],
        { duration: cfg.heightDuration, delay: leaveDelay, easing: cfg.heightEase, fill: 'both' }
      ));
    }

    if (!tx.anims.length) { tx.finish(); return; }
    Promise.all(tx.anims.map(function (a) { return a.finished; })).then(function () { tx.finish(); }, function () { tx.finish(); });
  }

  // The first render never animates: a root becomes "ready" two frames after init (after the first paint).
  function markReady(root) {
    if (root.hasAttribute('data-qk-filter-ready')) return;
    var go = function () { root.setAttribute('data-qk-filter-ready', ''); };
    if (window.requestAnimationFrame) requestAnimationFrame(function () { requestAnimationFrame(go); });
    else setTimeout(go, 50);
  }

  var listening = false;
  function init(scope) {
    var base = scope && scope.querySelectorAll ? scope : document;
    var roots = Array.prototype.slice.call(base.querySelectorAll('[data-qk-filter]'));
    if (base.matches && base.matches('[data-qk-filter]')) roots.unshift(base);
    roots.forEach(markReady);
    // Theme Editor only: a section that is added or re-rendered later brings its own roots. On a live storefront there is no listener.
    if (!listening && designMode()) {
      listening = true;
      document.addEventListener('shopify:section:load', function (e) { init(e.target); });
      document.addEventListener('shopify:section:unload', function (e) {
        if (!e.target || !e.target.querySelectorAll) return;
        Array.prototype.forEach.call(e.target.querySelectorAll('[data-qk-filter]'), settle);
      });
    }
  }

  window.QikifyFilterAnim = { update: update, settle: settle, init: init, canAnimate: canAnimate };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
})();
